package com.gastoncantera.intercom.call

import com.gastoncantera.intercom.push.dismissIncomingCallNotification
import com.gastoncantera.intercom.push.notifyIncomingCallHandledByUser
import com.shepeliev.webrtckmp.AudioTrack
import com.shepeliev.webrtckmp.IceCandidate
import com.shepeliev.webrtckmp.IceServer
import com.shepeliev.webrtckmp.MediaDevices
import com.shepeliev.webrtckmp.MediaStream
import com.shepeliev.webrtckmp.OfferAnswerOptions
import com.shepeliev.webrtckmp.PeerConnection
import com.shepeliev.webrtckmp.PeerConnectionState
import com.shepeliev.webrtckmp.RtcConfiguration
import com.shepeliev.webrtckmp.SessionDescription
import com.shepeliev.webrtckmp.SessionDescriptionType
import com.shepeliev.webrtckmp.VideoTrack
import com.shepeliev.webrtckmp.onConnectionStateChange
import com.shepeliev.webrtckmp.onIceCandidate
import com.shepeliev.webrtckmp.onTrack
import com.shepeliev.webrtckmp.toCanonicalString
import com.shepeliev.webrtckmp.videoTracks
import com.gastoncantera.intercom.isVideoSupportedInEmulator
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeout

enum class CallState { Idle, Calling, Incoming, Connected, AnsweredElsewhere, Ended }

private const val ANSWERED_ELSEWHERE_MS = 2500L
/** Si nadie contesta, el caller cuelga solo (evita quedar trabado en Calling). */
private const val RING_TIMEOUT_MS = 45_000L
/** Duración máxima de una llamada conectada (ambos lados). */
private const val CONNECTED_TIMEOUT_MS = 120_000L
/** Gracia tras `Disconnected` antes de colgar (p. ej. el otro mató la app). */
private const val DISCONNECTED_GRACE_MS = 5_000L
/** Máximo para que Firebase confirme el inicio de la llamada. */
private const val CALL_START_TIMEOUT_MS = 5_000L

/**
 * Orquesta una llamada de audio 1:1 con webrtc-kmp, usando [CallRepository] para el signaling.
 *
 * - Caller: [call] crea la offer, la publica y espera la answer + ICE.
 * - Callee: [listenForIncoming] detecta la offer; [accept] responde; [reject]/[hangup] cortan.
 */
class CallManager(
    private val repository: CallRepository,
    private val scope: CoroutineScope,
) {
    private val iceServers = listOf(
        IceServer(urls = listOf("stun:stun.l.google.com:19302")),
    )

    private val _state = MutableStateFlow(CallState.Idle)
    val state: StateFlow<CallState> = _state.asStateFlow()

    /** Cámara local de ic (auto-preview). */
    private val _localVideoTrack = MutableStateFlow<VideoTrack?>(null)
    val localVideoTrack: StateFlow<VideoTrack?> = _localVideoTrack.asStateFlow()

    /** Video remoto (la cámara de ic, que ve gc). */
    private val _remoteVideoTrack = MutableStateFlow<VideoTrack?>(null)
    val remoteVideoTrack: StateFlow<VideoTrack?> = _remoteVideoTrack.asStateFlow()

    /**
     * Audio remoto. En Android/iOS lo reproduce el motor nativo de WebRTC; en web hay que adjuntarlo
     * a un elemento de audio (ver RemoteAudioSink), por eso lo exponemos.
     */
    private val _remoteAudioTrack = MutableStateFlow<AudioTrack?>(null)
    val remoteAudioTrack: StateFlow<AudioTrack?> = _remoteAudioTrack.asStateFlow()

    /** Buzón (callId del depto) de la llamada activa/entrante. Solo informativo para la UI. */
    private val _incomingCallId = MutableStateFlow<String?>(null)
    val incomingCallId: StateFlow<String?> = _incomingCallId.asStateFlow()

    /** Último error de permisos/dispositivo al iniciar o contestar. La UI lo muestra y lo limpia. */
    private val _error = MutableStateFlow<CallError?>(null)
    val error: StateFlow<CallError?> = _error.asStateFlow()

    fun clearError() {
        _error.value = null
    }

    private var peerConnection: PeerConnection? = null
    private var localStream: MediaStream? = null
    private var callId: String? = null
    private var incomingSession: CallSession? = null

    /** Buzones que están escuchando los [incomingJobs] actuales. */
    private var listeningIds: Set<String> = emptySet()
    private val incomingJobs = mutableListOf<Job>()
    private val callJobs = mutableListOf<Job>()
    private val addedRemoteCandidates = mutableSetOf<String>()
    private var connectedTimeoutJob: Job? = null
    private var disconnectedGraceJob: Job? = null

    // -------- Caller --------

    suspend fun call(callId: String, caller: String, callee: String) {
        if (_state.value == CallState.Calling || _state.value == CallState.Connected) return
        this.callId = callId
        _error.value = null
        _state.value = CallState.Calling

        val pc = createPeerConnection()
        try {
            attachLocalMedia(pc, withVideo = true)
        } catch (e: Throwable) {
            _error.value = classifyMediaError(e)
            finish(CallState.Idle)
            return
        }
        collectLocalIce(pc) { repository.addCallerCandidate(callId, it) }
        collectRemoteCandidates(pc, repository.observeCalleeCandidates(callId))
        collectRemoteTracks(pc)
        observeConnectionState(pc)

        val offer = pc.createOffer(OfferAnswerOptions(offerToReceiveAudio = true))
        pc.setLocalDescription(offer)
        pc.capVideoSendBitrate()
        try {
            withTimeout(CALL_START_TIMEOUT_MS) {
                repository.startCall(callId, caller, callee, offer.toData())
            }
        } catch (e: Throwable) {
            finish(CallState.Idle)
            throw e
        }

        callJobs += scope.launch {
            delay(RING_TIMEOUT_MS)
            if (_state.value == CallState.Calling) hangup()
        }

        callJobs += scope.launch {
            repository.observeCall(callId).collect { session ->
                when {
                    session == null -> finish(CallState.Ended)
                    session.answer != null && pc.remoteDescription == null -> {
                        pc.setRemoteDescription(session.answer.toSessionDescription())
                    }
                }
            }
        }
    }

    // -------- Callee --------

    /**
     * Escucha varios buzones a la vez (un usuario puede estar en varios deptos/consorcios). El
     * primero que recibe una offer se vuelve la llamada activa.
     */
    fun listenForIncoming(callIds: List<String>) {
        // Re-suscribe si cambió el conjunto de buzones: la config viene de Firestore y puede cambiar
        // con la app abierta. Antes se salía si ya había listeners, así que agregarle un depto a un
        // residente no tenía efecto hasta que reiniciara: le llegaba el push (el doc de presencia sí
        // se actualiza) pero nadie escuchaba ese buzón y la llamada no aparecía en la UI.
        val ids = callIds.toSet()
        if (ids == listeningIds) return
        stopListening()
        listeningIds = ids
        ids.forEach { id ->
            incomingJobs += scope.launch {
                repository.observeCall(id).collect { session -> handleIncoming(id, session) }
            }
        }
    }

    private fun handleIncoming(id: String, session: CallSession?) {
        when {
            // Solo nos afecta el borrado del buzón de la llamada activa.
            session == null -> if (callId == id && _state.value != CallState.Idle) finish(CallState.Idle)
            session.offer != null && session.answer == null && _state.value == CallState.Idle -> {
                callId = id
                incomingSession = session
                _incomingCallId.value = id
                _state.value = CallState.Incoming
            }
            // Otro usuario/device atendió primero (nosotros seguíamos en Incoming). No tocamos el
            // doc, solo avisamos y volvemos a esperar.
            session.answer != null && callId == id && _state.value == CallState.Incoming -> {
                answeredElsewhere()
            }
        }
    }

    private fun answeredElsewhere() {
        notifyIncomingCallHandledByUser(callId)
        // Única salida del timbre que no pasa por finish(): el doc sigue vivo (la llamada la
        // atendió otro device), así que nadie más nos va a cortar el tono.
        dismissIncomingCallNotification()
        incomingSession = null
        _incomingCallId.value = null
        _state.value = CallState.AnsweredElsewhere
        scope.launch {
            delay(ANSWERED_ELSEWHERE_MS)
            if (_state.value == CallState.AnsweredElsewhere) {
                _state.value = CallState.Idle
            }
        }
    }

    suspend fun accept() {
        val id = callId ?: return
        val session = incomingSession ?: return
        val offer = session.offer ?: return

        // Seguimos en Incoming hasta ganar tryAnswerCall: así el collector sigue pudiendo
        // pasar a AnsweredElsewhere si otro device escribe la answer primero.
        _error.value = null
        notifyIncomingCallHandledByUser(id)
        dismissIncomingCallNotification()

        val pc = createPeerConnection()
        try {
            attachLocalMedia(pc, withVideo = false)
        } catch (e: Throwable) {
            _error.value = classifyMediaError(e)
            hangup()
            return
        }
        collectLocalIce(pc) { repository.addCalleeCandidate(id, it) }
        collectRemoteCandidates(pc, repository.observeCallerCandidates(id))
        collectRemoteTracks(pc)
        observeConnectionState(pc)

        pc.setRemoteDescription(offer.toSessionDescription())
        val answer = pc.createAnswer(OfferAnswerOptions(offerToReceiveAudio = true))
        pc.setLocalDescription(answer)

        if (!repository.tryAnswerCall(id, answer.toData())) {
            // Perdimos el claim: soltamos WebRTC local, no endCall (la llamada buena sigue).
            discardLocalPeer()
            if (_state.value == CallState.Incoming) answeredElsewhere()
            return
        }
        _state.value = CallState.Connected
    }

    /** Cierra PC/tracks locales sin tocar el doc de Firestore. */
    private fun discardLocalPeer() {
        _remoteVideoTrack.value = null
        _remoteAudioTrack.value = null
        _localVideoTrack.value = null
        cancelCallJobs()
        peerConnection?.close()
        peerConnection = null
        localStream?.release()
        localStream = null
        addedRemoteCandidates.clear()
    }

    suspend fun reject() = hangup()

    suspend fun onAppBackground() {
        if (_state.value == CallState.Calling || _state.value == CallState.Connected) {
            hangup()
        }
    }

    // -------- Compartido --------

    suspend fun hangup() {
        if (_state.value == CallState.Incoming) {
            notifyIncomingCallHandledByUser(callId)
        }
        callId?.let { repository.endCall(it) }
        finish(CallState.Idle)
    }

    /** Cancela los listeners de entrantes (al salir de la pantalla del callee). */
    fun stopListening() {
        incomingJobs.forEach { it.cancel() }
        incomingJobs.clear()
        listeningIds = emptySet()
    }

    private fun finish(newState: CallState) {
        // Null primero para que Compose desmonte los renders (removeSink/removeRenderer)
        // antes de cerrar el pc y liberar el stream.
        _remoteVideoTrack.value = null
        _remoteAudioTrack.value = null
        _localVideoTrack.value = null
        cancelCallJobs()
        peerConnection?.close()
        peerConnection = null
        localStream?.release()
        localStream = null
        incomingSession = null
        _incomingCallId.value = null
        addedRemoteCandidates.clear()
        dismissIncomingCallNotification()
        _state.value = newState
    }

    // -------- Helpers WebRTC --------

    private fun createPeerConnection(): PeerConnection {
        val pc = PeerConnection(RtcConfiguration(iceServers = iceServers))
        peerConnection = pc
        return pc
    }

    private suspend fun attachLocalMedia(pc: PeerConnection, withVideo: Boolean) {
        val withVideo = withVideo && isVideoSupportedInEmulator()
        requireMediaPermission(withVideo)
        val stream = MediaDevices.getUserMedia {
            audio()
            if (withVideo) applyVideoCaptureConstraints()
        }
        localStream = stream
        stream.tracks.forEach { pc.addTrack(it, stream) }
        if (withVideo) {
            _localVideoTrack.value = stream.videoTracks.firstOrNull()
        }
    }

    private fun collectRemoteTracks(pc: PeerConnection) {
        callJobs += scope.launch {
            pc.onTrack.collect { event ->
                when (val track = event.track) {
                    is VideoTrack -> _remoteVideoTrack.value = track
                    is AudioTrack -> _remoteAudioTrack.value = track
                    else -> {}
                }
            }
        }
    }

    private fun collectLocalIce(pc: PeerConnection, send: suspend (IceCandidateData) -> Unit) {
        callJobs += scope.launch {
            pc.onIceCandidate.collect { send(it.toData()) }
        }
    }

    private fun collectRemoteCandidates(pc: PeerConnection, flow: Flow<List<IceCandidateData>>) {
        callJobs += scope.launch {
            flow.collect { candidates ->
                candidates.forEach { c ->
                    val key = "${c.sdpMid}|${c.sdpMLineIndex}|${c.candidate}"
                    if (addedRemoteCandidates.add(key)) {
                        pc.addIceCandidate(c.toIceCandidate())
                    }
                }
            }
        }
    }

    private fun observeConnectionState(pc: PeerConnection) {
        callJobs += scope.launch {
            pc.onConnectionStateChange.collect { st ->
                when (st) {
                    PeerConnectionState.Connected -> {
                        _state.value = CallState.Connected
                        disconnectedGraceJob?.cancel()
                        disconnectedGraceJob = null
                        startConnectedTimeout()
                    }
                    PeerConnectionState.Disconnected -> startDisconnectedGrace()
                    PeerConnectionState.Failed,
                    PeerConnectionState.Closed -> hangup()
                    else -> {}
                }
            }
        }
    }

    private fun startConnectedTimeout() {
        if (connectedTimeoutJob != null) return
        connectedTimeoutJob = scope.launch {
            delay(CONNECTED_TIMEOUT_MS)
            if (_state.value == CallState.Connected) hangup()
        }
    }

    private fun startDisconnectedGrace() {
        if (disconnectedGraceJob != null) return
        disconnectedGraceJob = scope.launch {
            delay(DISCONNECTED_GRACE_MS)
            hangup()
        }
    }

    private fun cancelCallJobs() {
        callJobs.forEach { it.cancel() }
        callJobs.clear()
        connectedTimeoutJob?.cancel()
        connectedTimeoutJob = null
        disconnectedGraceJob?.cancel()
        disconnectedGraceJob = null
    }

    // -------- Mapeo modelos signaling <-> webrtc-kmp --------

    private fun SessionDescription.toData() =
        SessionDescriptionData(type = type.toCanonicalString(), sdp = sdp)

    private fun SessionDescriptionData.toSessionDescription() = SessionDescription(
        type = when (type) {
            "offer" -> SessionDescriptionType.Offer
            "answer" -> SessionDescriptionType.Answer
            "pranswer" -> SessionDescriptionType.Pranswer
            else -> SessionDescriptionType.Rollback
        },
        sdp = sdp,
    )

    private fun IceCandidate.toData() =
        IceCandidateData(sdpMid = sdpMid, sdpMLineIndex = sdpMLineIndex, candidate = candidate)

    private fun IceCandidateData.toIceCandidate() =
        IceCandidate(sdpMid = sdpMid, sdpMLineIndex = sdpMLineIndex, candidate = candidate)
}
